import React from "react";
import {
  ColumnSearchType,
  CreditorApprovalInfoTableColumn,
} from "us.collection.admin/components/CreditorApproval/Constants";
import { i18n } from "us.helper";
import { ColumnSearchTypeProps } from "us.collection.admin/components/CreditorApproval/Interfaces";
import { ITableTreeColumns } from "us.common/components";
/**
 * @description - Get type of column (`IColumnSearchType`) by column id
 * @param key - Column title
 * @returns column type - `IColumnSearchType`
 */
export const getColumnType = (key: string): ColumnSearchTypeProps => {
  try {
    return key == CreditorApprovalInfoTableColumn.CREDITOR_NUMBER ||
      CreditorApprovalInfoTableColumn.CREDITOR_GROUP
      ? ColumnSearchType.NUMBER
      : ColumnSearchType.STRING;
  } catch (error) {
    return ColumnSearchType.STRING;
  }
};

/**
 * @description Get table header column title as a string
 * @param dataIndex - index of the column
 * @param title - title of the column (can be a translation key or a plain string)
 * @returns string
 */
export function getHeaderTitle({ title }: { title: string }) {
  try {
    let label = title;
    if (title) {
      if (/^[A-Z_]+$/.test(title)) {
        label = i18n.t(`US.COLLECTION.ADMIN:CREDITOR_APPROVAL.${title}`);
      } else {
        label = i18n.t(title);
      }
    }
    return label;
  } catch (error) {
    console.error("getHeaderTitle error:", error);
    return title;
  }
}

/**
 * @description Generate table columns for TableTree with translation, sorting, and filtering
 * @param COLUMNS - Array of column config objects
 * @param onCallAction -  callback for row action
 * @param setSelectedRowKey -  to set selected row key
 * @returns ITableTreeColumns - array of column definitions for TableTree
 */

export const getCreditorApprovalInfoTableColumns = (
  COLUMNS: any,
  onCallAction: (record: any) => void,
  setSelectedRowKey: (key: string) => void
): ITableTreeColumns => {
  try {
    const columns: ITableTreeColumns = [];

    COLUMNS.map(({ key, title }: any) => {
      let column: any = {
        key,
        dataIndex: key,
        title: getHeaderTitle({ title }),
        ellipsis: { showTitle: false },
        customFilter: true,
        customSorter: (a: any, b: any) => a.localeCompare(b),
      };

      if (key === CreditorApprovalInfoTableColumn.CREDITOR_GROUP) {
        column.customRenderChild = (text: any, record: any) => (
          <a
            href={`${window._ENV.REACT_APP_COLLECTION_URL}/creditor-group/${record?.creditorGroupNo}/bmd`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {record[key] ?? ""}
          </a>
        );
      } else if (
        key === CreditorApprovalInfoTableColumn.CREDITOR_NUMBER &&
        onCallAction
      ) {
        column.customRenderChild = (text: any, record: any) => (
          <span
            style={{ cursor: "pointer", color: "#49409A" }}
            onClick={() => {
              setSelectedRowKey?.(record.requestId);
              onCallAction(record);
            }}
          >
            {record[key] ?? ""}
          </span>
        );
      } else {
        column.customRenderChild = (text: any, record: any) => (
          <span>{record[key] ?? ""}</span>
        );
      }

      columns.push(column);
    });

    return columns;
  } catch (error) {
    return [];
  }
};
/**
 * @description Generate table columns for TableTree with translation, sorting, and filtering
 * @param COLUMNS - Array of column config objects
 * @returns ITableTreeColumns - array of column definitions for TableTree
 */

export const getContactPersonInfoTableColumns = (
  COLUMNS: any,
  tableData: any
): ITableTreeColumns => {
  try {
    const columns: ITableTreeColumns = [];

    const columnRules: Record<string, { min: number; max: number }> = {
      firstName: { min: 150, max: 250 },
      lastName: { min: 150, max: 250 },
      personNo: { min: 150, max: 250 },
      address1: { min: 250, max: 400 },
      address2: { min: 250, max: 400 },
      postalCode: { min: 150, max: 250 },
      email: { min: 200, max: 400 },
      telWork: { min: 150, max: 200 },
      telMobile: { min: 150, max: 200 },
    };
    COLUMNS.map(({ key, title }: any) => {
      let column: any = {
        key,
        dataIndex: key,
        title: (
          <span style={{ whiteSpace: "nowrap" }}>
            {getHeaderTitle({ title })}
          </span>
        ),
        ellipsis: { showTitle: false },
        customFilter: true,
        customSorter: (a: any, b: any) =>
          a?.[key]?.toString().localeCompare(b?.[key]?.toString()),
      };

      const longest = Math.max(
        ...tableData.map((row: any) =>
          row[key] ? row[key].toString().length : 0
        )
      );

      let calculatedWidth = longest * 8 + 40;
      const { min, max } = columnRules[key];

      if (calculatedWidth < min) calculatedWidth = min;
      if (calculatedWidth > min) calculatedWidth = calculatedWidth;
      if (calculatedWidth > max) calculatedWidth = max;

      column.width = calculatedWidth;

      column.onCell = () => ({
        style: {
          whiteSpace: "nowrap",
          paddingRight: "6px",
        },
      });

      column.customRenderChild = (text: any, record: any) => (
        <span>{record[key] ?? ""}</span>
      );

      columns.push(column);
    });

    return columns;
  } catch (error) {
    return [];
  }
};
